import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Grid, Alert, FormControl, Slider } from '@mui/material';
import { Person as PersonIcon, Warning as WarningIcon } from '@mui/icons-material';
import EmptyStateCard from 'app/components/ScenarioEditorPage/components/EmptyStateCard.tsx';

import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isUserSuperAdminSelector, CurrentUserSelector } from 'app/selectors/userSelectors';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nValidationNs, I18nCoreNs } from '@/i18n/dictionaries/interfaces';
import { getLanguageDisplayName } from '@/app/utils/languageUtils';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { AccessLevel as AccessLevelType } from '@/api-gen/api.schemas';

import type { ScenarioSettingsType } from '../hooks/useScenarioForm';
import type { PersonaCompanyAccessInfoDto, PersonaDto, PersonaUsage } from '@/api-gen/api.schemas';
import { PersonaUsage as PersonaUsageEnum } from '@/api-gen/api.schemas';

import LanguageSelector from 'app/components/form/LanguageSelector.tsx';
import SectionLabel from 'app/components/ScenarioEditorPage/components/SectionLabel';
import ScenarioPersonaCard from './ScenarioPersonaCard';
import { SharePersonaDialogContent } from './SharePersonaDialogContent';
import PersonaSelectionDialog from './PersonaSelectionDialog';
import { useScenarioPersonas } from './hooks/useScenarioPersonas';
import PersonaDetailDialog from '../../../PersonasManager/PersonaDetailDialog/PersonaDetailDialog';
import ConfirmDialog from '@/app/components/ConfirmDialog/ConfirmDialog';
import {
  usePersonaSharePersonaWithCompanies,
  getScenariosGetMissingPersonaCompanyAccessQueryOptions,
  scenariosGetMissingPersonaCompanyAccess
} from '@/api-gen/api';
import { addError, addInfo } from 'app/actions/snackbar/snackbar';
import DashedAddButton from 'app/components/form/DashedAddButton';

type ShareDialogState = {
  persona: PersonaDto;
  usages: PersonaUsage[];
  defaultRoleId?: number;
  missingCompanyIds: number[];
  missingCompanyNames: string[];
};

const ScenarioPersonasSettings: React.FC = () => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateValidation] = useTranslation([I18nNamespace.Validation]);
  const [translateCore] = useTranslation([I18nNamespace.Core]);
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitted }
  } = useFormContext<ScenarioSettingsType>();
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const currentUser = useSelector(CurrentUserSelector);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isSelectionDialogOpen, setIsSelectionDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPersonaForEdit, setSelectedPersonaForEdit] = useState<PersonaDto | null>(null);
  const [shareDialogState, setShareDialogState] = useState<ShareDialogState | null>(null);
  const [isCheckingPersonaShare, setIsCheckingPersonaShare] = useState(false);
  const [isSharingPersona, setIsSharingPersona] = useState(false);
  const sharePersonaMutation = usePersonaSharePersonaWithCompanies();

  // Watch current scenario personas and company
  const scenarioPersonas = watch('scenarioPersonas') || [];
  const scenarioCompanyId = watch('companyId') ?? currentUser?.companyId;
  const scenarioId = watch('id');

  // Utility function to get user's access level to a persona
  const getUserAccessLevel = (persona: PersonaDto): AccessLevelType | undefined => {
    // Superadmins always have full access
    if (isSuperAdmin) return AccessLevel.CanAdministrate;

    // Regular users: find their company's access in the array
    const userCompanyId = currentUser?.companyId;
    if (!userCompanyId) return undefined;

    const companyAccess = persona.companies?.find((c) => c.companyId === userCompanyId);
    return companyAccess?.access;
  };

  // Shared persona operations (unified across tabs)
  const { addPersona, removePersona, setDefaultPersona, setRoleId, hasDefaultPersonaError } = useScenarioPersonas(
    scenarioPersonas,
    setValue,
    trigger,
    translate
  );

  const handleAddPersona = () => {
    setIsSelectionDialogOpen(true);
  };

  const tryAddPersonaToScenario = (personaToAdd: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number) => {
    const result = addPersona(personaToAdd, usages, defaultRoleId);
    if (!result.success) {
      console.error('Failed to add persona:', result.message);
      dispatch(
        addError({
          errorText:
            result.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError))
        })
      );
      return false;
    }

    setIsSelectionDialogOpen(false);
    return true;
  };

  const handlePersonaSelect = async (persona: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number) => {
    if (isCheckingPersonaShare || isSharingPersona) {
      return;
    }

    const personaId = persona.id;
    if (!personaId) {
      dispatch(
        addError({
          errorText: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError))
        })
      );
      return;
    }

    // For new scenarios (id 0 or undefined), skip sharing validation
    if (!scenarioId) {
      tryAddPersonaToScenario(persona, usages, defaultRoleId);
      return;
    }

    setIsCheckingPersonaShare(true);
    try {
      const queryOptions = getScenariosGetMissingPersonaCompanyAccessQueryOptions(scenarioId, personaId, {
        query: {
          staleTime: 30 * 1000
        }
      });

      const response = await scenariosGetMissingPersonaCompanyAccess(scenarioId, personaId);
      queryClient.setQueryData(queryOptions.queryKey, response);
      const data = response.data;
      const missingCompanyIds = data?.missingCompanyIds ?? [];
      const missingCompanyNames = data?.missingCompanyNames ?? [];

      if (!missingCompanyIds.length) {
        tryAddPersonaToScenario(persona, usages, defaultRoleId);
        return;
      }

      setShareDialogState({
        persona,
        usages,
        defaultRoleId,
        missingCompanyIds,
        missingCompanyNames
      });
      setIsSelectionDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to validate persona sharing', error);
      dispatch(
        addError({
          errorText:
            error?.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaCheckError))
        })
      );
    } finally {
      setIsCheckingPersonaShare(false);
    }
  };

  const handleShareDialogClose = () => {
    if (isSharingPersona) {
      return;
    }
    setShareDialogState(null);
  };

  const handleShareConfirm = async () => {
    if (!shareDialogState?.persona.id) {
      dispatch(
        addError({
          errorText: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError))
        })
      );
      setShareDialogState(null);
      return;
    }

    const personaName =
      shareDialogState.persona.name ||
      translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona));

    setIsSharingPersona(true);
    try {
      await sharePersonaMutation.mutateAsync({
        id: shareDialogState.persona.id,
        data: shareDialogState.missingCompanyIds.map((companyId) => ({
          companyId,
          access: AccessLevel.CanRead
        }))
      });

      const added = tryAddPersonaToScenario(
        shareDialogState.persona,
        shareDialogState.usages,
        shareDialogState.defaultRoleId
      );

      if (added) {
        dispatch(
          addInfo({
            infoText: translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaConfirmSuccess),
              { personaName }
            )
          })
        );
        setShareDialogState(null);
      } else {
        setShareDialogState(null);
      }
    } catch (error: any) {
      console.error('Failed to share persona with missing companies', error);
      dispatch(
        addError({
          errorText:
            error?.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError))
        })
      );
    } finally {
      setIsSharingPersona(false);
    }
  };

  const handleRemovePersona = (personaId: number) => {
    removePersona(personaId);
  };

  const handleEditPersona = (persona: PersonaDto) => {
    setSelectedPersonaForEdit(persona);
    setIsEditDialogOpen(true);
  };

  const existingPersonaIds = scenarioPersonas.map((sp) => sp.personaId).filter((id): id is number => id != null);

  const languageMismatches = (() => {
    const scenarioLang = watch('langCode');
    if (!scenarioLang) return [] as { id: number; name: string }[];
    return scenarioPersonas
      .filter((sp) => sp.persona?.languageCode && sp.persona.languageCode !== scenarioLang)
      .map((sp) => ({ id: sp.personaId!, name: sp.persona?.name || `#${sp.personaId}` }));
  })();

  const shareDialogPersonaName =
    shareDialogState != null
      ? shareDialogState.persona.name ||
        translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona))
      : '';

  const renderEmptyState = () => (
    <EmptyStateCard
      icon={<PersonIcon />}
      title={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.noPersonas))}
      description={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonasDescription))}
      buttonLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addFirstPersona))}
      onAction={handleAddPersona}
    />
  );

  const renderPersonasList = () => (
    <Box>
      <Grid container spacing={2}>
        {scenarioPersonas.map((scenarioPersona) => {
          return (
            <Grid item xs={12} md={6} lg={4} key={scenarioPersona.personaId}>
              <ScenarioPersonaCard
                scenarioPersona={scenarioPersona}
                onRemove={handleRemovePersona}
                onSetDefault={(personaId) => setDefaultPersona(personaId)}
                onEdit={handleEditPersona}
                scenarioLanguage={watch('langCode')}
                scenarioCompanyId={scenarioCompanyId}
                onChangeRoleId={(personaId, roleId) => setRoleId(personaId, roleId)}
              />
            </Grid>
          );
        })}
        <Grid item xs={12} md={6} lg={4}>
          <DashedAddButton onClick={handleAddPersona} fullHeight>
            {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersona))}
          </DashedAddButton>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ mt: 5, mb: 3, px: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ width: 300 }}>
          <LanguageSelector
            size="small"
            name={nameof.full<ScenarioSettingsType>((f) => f.langCode)}
            control={control}
            label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.language))}
            error={!!errors.langCode}
          />
        </Box>
        <FormControl sx={{ width: 300 }}>
          <Typography variant="body2" id="voice-speed-slider-label" gutterBottom>
            {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.voiceSpeed))}
          </Typography>
          <Controller
            name="speed"
            control={control}
            render={({ field: fieldProps }) => (
              <Slider
                {...fieldProps}
                aria-labelledby="voice-speed-slider-label"
                min={0.7}
                max={1.2}
                step={0.05}
                marks
                valueLabelDisplay="auto"
              />
            )}
          />
        </FormControl>
      </Box>
      <Controller
        control={control}
        name="scenarioPersonas"
        render={() => (
          <>
            <SectionLabel sx={{ mb: 1.5 }}>
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.title))}
            </SectionLabel>

            {/* Validation Alert */}
            {isSubmitted && (hasDefaultPersonaError || languageMismatches.length > 0) && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
                {hasDefaultPersonaError && (
                  <Box sx={{ mb: languageMismatches.length ? 1 : 0 }}>
                    {scenarioPersonas.length === 0
                      ? translateValidation(
                          nameof.full<I18nValidationNs>((n) => n.personaValidation.noPersonasGuidance)
                        )
                      : translateValidation(
                          nameof.full<I18nValidationNs>((n) => n.personaValidation.defaultPersonaRequiredGuidance)
                        )}
                  </Box>
                )}
                {languageMismatches.length > 0 && (
                  <Box>
                    {translateValidation(
                      nameof.full<I18nValidationNs>((n) => n.personaValidation.languageMismatchDetail),
                      {
                        scenarioLanguage: getLanguageDisplayName(watch('langCode'), translateCore),
                        personaList: languageMismatches.map((p) => p.name).join(', ')
                      }
                    )}
                  </Box>
                )}
              </Alert>
            )}

            {scenarioPersonas.length === 0 ? renderEmptyState() : renderPersonasList()}

            <PersonaSelectionDialog
              open={isSelectionDialogOpen}
              onClose={() => setIsSelectionDialogOpen(false)}
              onPersonaSelect={handlePersonaSelect}
              existingPersonaIds={existingPersonaIds}
              scenarioCompanyId={scenarioCompanyId}
              scenarioLanguage={watch('langCode')}
              isProcessingSelection={isCheckingPersonaShare}
            />

            <PersonaDetailDialog
              open={isEditDialogOpen}
              onClose={() => {
                setIsEditDialogOpen(false);
                setSelectedPersonaForEdit(null);
              }}
              personaId={selectedPersonaForEdit?.id}
              onRefresh={() => {
                // No-op: optimistic path handled via onPersonaUpdated
              }}
              onPersonaUpdated={(updated) => {
                // Optimistically update form state for matching personaId
                const updatedList = (scenarioPersonas || []).map((sp) =>
                  sp.personaId === updated.id ? { ...sp, persona: { ...sp.persona, ...updated } } : sp
                );
                setValue('scenarioPersonas', updatedList as any);
                // Trigger validation to refresh any mismatch warnings
                trigger('scenarioPersonas');
              }}
            />
            <ConfirmDialog
              open={!!shareDialogState}
              title={
                shareDialogState
                  ? translate(
                      nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaTitle),
                      { personaName: shareDialogPersonaName }
                    )
                  : undefined
              }
              onConfirm={handleShareConfirm}
              onCancel={handleShareDialogClose}
              confirmButtonDisabled={isSharingPersona}
              cancelButtonDisabled={isSharingPersona}
              confirmLoading={isSharingPersona}
            >
              {shareDialogState && (
                <SharePersonaDialogContent
                  persona={shareDialogState.persona}
                  missingCompanyNames={
                    shareDialogState.missingCompanyNames?.length
                      ? shareDialogState.missingCompanyNames
                      : shareDialogState.missingCompanyIds.map((id) => `#${id}`)
                  }
                />
              )}
            </ConfirmDialog>
          </>
        )}
      />
    </Box>
  );
};

export default ScenarioPersonasSettings;
